/**
 * An author-pasted video link → what a player iframe needs. Pure, so the
 * picker of link shapes is tested (video.test.ts) and shared by the server
 * render and the client facade.
 *
 * Its sibling ./youtube.ts scans rich-text HTML for YouTube links; this reads
 * ONE link an author typed into a field, and knows Vimeo too. Both embed
 * through youtube-nocookie.com, the privacy-enhanced host.
 *
 * Anything not recognised is `null` — the caller renders its still image, never
 * an iframe pointed at a URL nobody vetted.
 */

export type VideoEmbed = {
  provider: "youtube" | "vimeo";
  id: string;
  /** Unlisted Vimeo videos carry a privacy hash (`/123/abc` or `?h=abc`). */
  hash?: string;
  /** Start offset in whole seconds (0 = from the beginning). */
  start: number;
};

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
]);
const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d{1,12}$/;
const VIMEO_HASH = /^[A-Za-z0-9]{6,32}$/;

/** "90", "90s", "1m30s", "1h2m3s" → seconds; anything else → 0. */
export function parseStart(raw: string | null | undefined): number {
  const v = (raw ?? "").trim().toLowerCase();
  if (/^\d+s?$/.test(v)) return parseInt(v, 10);
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(v);
  if (!m || !v) return 0;
  return (Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0)) * 60 + Number(m[3] ?? 0);
}

export function parseVideoUrl(raw: string | null | undefined): VideoEmbed | null {
  let url: URL;
  try {
    url = new URL((raw ?? "").trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase();
  const parts = url.pathname.split("/").filter(Boolean);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));

  if (YOUTUBE_HOSTS.has(host)) {
    const id =
      host === "youtu.be"
        ? parts[0]
        : parts[0] === "watch"
          ? url.searchParams.get("v")
          : ["embed", "shorts", "live", "v"].includes(parts[0] ?? "")
            ? parts[1]
            : null;
    if (!id || !YOUTUBE_ID.test(id)) return null;
    const start = parseStart(url.searchParams.get("t") ?? url.searchParams.get("start"));
    return { provider: "youtube", id, start };
  }

  if (VIMEO_HOSTS.has(host)) {
    const [id, hash] = host === "player.vimeo.com" && parts[0] === "video" ? parts.slice(1) : parts;
    if (!id || !VIMEO_ID.test(id)) return null;
    const h = url.searchParams.get("h") ?? hash;
    return {
      provider: "vimeo",
      id,
      ...(h && VIMEO_HASH.test(h) ? { hash: h } : {}),
      start: parseStart(hashParams.get("t")),
    };
  }

  return null;
}

/** The player URL for an embed. `autoplay` is for the click-to-play facade:
 *  the visitor already pressed play, so the player should not ask again. */
export function videoEmbedSrc(v: VideoEmbed, opts: { autoplay?: boolean } = {}): string {
  const q = new URLSearchParams();
  if (opts.autoplay) q.set("autoplay", "1");
  if (v.provider === "youtube") {
    q.set("rel", "0");
    q.set("playsinline", "1");
    if (v.start > 0) q.set("start", String(v.start));
    return `https://www.youtube-nocookie.com/embed/${v.id}?${q}`;
  }
  if (v.hash) q.set("h", v.hash);
  q.set("dnt", "1");
  return `https://player.vimeo.com/video/${v.id}?${q}${v.start > 0 ? `#t=${v.start}s` : ""}`;
}
